import { IQService } from 'angular';
import * as _ from 'underscore';
import { ITypeaheadSource } from '../directives/tbTypeahead';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, RemoteAgentErrorCode, SchedulingErrorCode, UnifiedDatasourceErrorCode, WebClientErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { DatasourceDefinition, WorkbookDefinition } from '../models/ContentDefinitions';
import { DatasourceContentType, WorkbookContentType } from '../models/ContentTypes';
import { IContentInfo } from '../models/IContentInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IWorkbookBase, IWorkbookInfo } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { IExtractItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataSources } from '../resources/Datasources';
import { Workbooks } from '../resources/Workbooks';
import { ExtractTaskType } from '../ServerApi/types/IExtractTask';
import { IResult } from '../ServerApi/types/IResponse';
import { ISchedule, ScheduleActions } from '../ServerApi/types/ISchedule';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog, IConfirmActionDialogButton } from './ConfirmActionDialog';
import { IErrorDetail, INotificationTranslationIds } from './ContentActionNotification';
import { ContentUrl } from './ContentUrl';
import { ModalService, ModalSize } from './ModalService';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { ServerService } from './ServerService';

export enum CreateExtractTasksMode {
  AllowRefreshNow,
  AllowSchedule,
  AllowBoth
}

export interface IExtractTaskTypeOption {
  labelTranslateKey: string;
  value: string;
  tbTestId: string;
}

export interface IExtractTaskSchedule {
  schedule?: ISchedule;
  type?: string;
}

export interface ICreateExtractTasksScope extends ng.IScope {

  dialog: {
    mode: string;   // See DialogMode
    showModeSelector: boolean;
  };

  extractTask: IExtractTaskSchedule;

  schedulePrompt: string;
  scheduleSource: ITypeaheadSource;

  refreshNowPrompt: string;

  allowIncremental: boolean;
  extractTaskTypeOptions?: IExtractTaskTypeOption[];

  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
}

const DialogMode = {
  RefreshNow: 'RefreshNow',
  ScheduleRefresh: 'ScheduleRefresh'
};

type ExtractType = IWorkbookBase | IDataSourceBase;

export class CreateExtractTasksAction {

  public static $inject = [
    'ConfirmActionDialog',
    'ContentUrl',
    '$translate',
    'Workbooks',
    'DataSources',
    'SearchSuggestionsService',
    'ActionHelpers',
    'ModalService',
    'scheduleFrequencyDescriptionFilter',
    'ServerService',
    '$q'
  ];

  private notificationTranslationIds: INotificationTranslationIds = {
    successId: 'extractTasksCreateSuccess',
    errorId: 'extractTasksCreateError'
  };

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private ContentUrl: ContentUrl,
    private $translate: ng.translate.ITranslateService,
    private Workbooks: Workbooks,
    private Datasources: DataSources,
    private SearchSuggestionsService: SearchSuggestionsService,
    private ActionHelpers: ActionHelpers,
    private ModalService: ModalService,
    private scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    private server: ServerService,
    private $q: IQService
  ) { }

  // Generic logic

  public populateScope(scope: ICreateExtractTasksScope,
                        items: Set<IExtractItemInfo>,
                        schedulePrompt: string,
                        allowedMode: CreateExtractTasksMode,
                        refreshNowPrompt: string
  ) {
    scope.extractTask = {
      type: ExtractTaskType.RefreshExtract
    };

    scope.schedulePrompt = schedulePrompt;
    scope.scheduleSource = this.SearchSuggestionsService.schedulesSource(ScheduleActions.Extract);

    scope.allowIncremental = _.any(items.toArray(), (item: IExtractItemInfo) => item.hasIncrementalExtract);
    if(scope.allowIncremental) {
      scope.extractTaskTypeOptions = [
        { value: ExtractTaskType.RefreshExtract,
          labelTranslateKey: 'extractTasksTypeRefreshExtract',
          tbTestId: 'create-extract-refresh-type-full'
        },
        { value: ExtractTaskType.IncrementExtract,
          labelTranslateKey: 'extractTasksTypeIncrementExtract',
          tbTestId: 'create-extract-refresh-type-incremental'
        }
      ];
    }

    scope.dialog = {
      mode: allowedMode === CreateExtractTasksMode.AllowSchedule ? DialogMode.ScheduleRefresh : DialogMode.RefreshNow,
      showModeSelector: allowedMode === CreateExtractTasksMode.AllowBoth
    };

    scope.refreshNowPrompt = refreshNowPrompt;
  }

  private execute<T extends IExtractItemInfo>(
    items: Set<T>,
    context: IActionContext,
    schedulePrompt: string,
    scheduleRefreshAction: (ids: string[], scheduleId: string, type: string) => ng.IPromise<IResult>,
    refreshNowPrompt: string,
    refreshNowAction: (ids: string[], type: string) => ng.IPromise<IResult>,
    errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail,
    allowedMode: CreateExtractTasksMode
  ): ng.IPromise<IResult> {

    const scope = <ICreateExtractTasksScope> context.scope.$new();
    this.populateScope(scope, items, schedulePrompt, allowedMode, refreshNowPrompt);

    // Change the available buttons based on the dialog mode

    const buttons: IConfirmActionDialogButton[] = [];
    scope.$watch(() => scope.dialog.mode, () => {
      buttons.length = 0;
      if (scope.dialog.mode === DialogMode.ScheduleRefresh) {
        buttons.push({
          label: this.$translate.instant('extractTasksCreateConfirm'),
          action: () => scheduleRefreshAction(items.keys(), scope.extractTask.schedule.id, scope.extractTask.type),
          disabled: () => !scope.extractTask.schedule || !scope.extractTask.type
        });
      }
      else {
        buttons.push({
          label: this.$translate.instant('fullRefresh'),
          action: () => refreshNowAction(items.keys(), ExtractTaskType.RefreshExtract)
        });
        if (scope.allowIncremental) {
          buttons.push({
            label: this.$translate.instant('incrementalRefresh'),
            action: () => refreshNowAction(items.keys(), ExtractTaskType.IncrementExtract)
          });
        }
      }
    });

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = this.scheduleFrequencyDescriptionFilter(schedule);
      if (!schedule.parallel) {
        return this.$translate.instant('serialScheduleDescription', {
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: this.$translate.instant('serialScheduleDescriptionSuffix')
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = this.server.displayScheduleDescriptionAsName();

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      position: { reserveHeight: 550 },
      bodyTemplateUrl: 'confirmActionDialogExtractRefresh.html',
      title: this.dialogTitle(allowedMode),
      buttons: buttons,
      size: ModalSize.large
    });

    dialogInstance.result
      .then(this.ActionHelpers.notifyStandardError(items, this.notificationTranslationIds,
        (errorItem: IErrorItem<T>) => errorDetailFn(scope.extractTask.schedule)(errorItem)))
      .finally(() => scope.$destroy());

    return dialogInstance.result;
  }

  private dialogTitle(allowedMode: CreateExtractTasksMode) {
    switch(allowedMode) {
      case CreateExtractTasksMode.AllowBoth:
        return this.$translate.instant('extractTasksDialogTitle_AllowBoth');
      case CreateExtractTasksMode.AllowRefreshNow:
        return this.$translate.instant('extractTasksDialogTitle_AllowRefreshNow');
      case CreateExtractTasksMode.AllowSchedule:
        return this.$translate.instant('extractTasksDialogTitle_AllowSchedule');
      default:
        throw 'Unknown allowed mode: ' + allowedMode;
    }
  }

  private createExtractErrorMessageFor(errorItem: IErrorItem<IExtractItemInfo>, schedule: ISchedule) {
    switch(errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return this.$translate.instant('scheduleNotFound', { SCHEDULE: schedule.name });
      case WebClientErrorCode.NotSupportedForRemotelyRefreshedContent:
        return this.$translate.instant('refreshNowRemotelyNotSupported');
    }
    return this.$translate.instant('errorUnknown');
  }

  // Workbook specific logic

  public forWorkbook(workbook: IWorkbookBase, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const workbookSet = Set.singleton(workbook, (wb: IWorkbookInfo) => wb.id);
    return this.forWorkbooks(workbookSet, context, allowedMode);
  }

  public forWorkbooks(workbooks: Set<IWorkbookBase>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const schedulePrompt = (workbooks.size() === 1)
        ? this.$translate.instant('schedulesLabelPromptWorkbook', { WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name) })
        : this.$translate.instant('schedulesLabelPromptWorkbooks', { COUNT: workbooks.size() }, L10n.Interpolations.Count);

    const refreshNowPrompt =
      this.$translate.instant('extractTasksRefreshNowOnWorkbooksPrompt', {
        WORKBOOK_COUNT: workbooks.size(),
        WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name)
      }, L10n.Interpolations.Count);

    return this.execute(
      workbooks,
      context,
      schedulePrompt,
      (ids: string[], scheduleId: string, type: string) => this.Workbooks.createExtractTask(ids, scheduleId, type),
      refreshNowPrompt,
      (ids: string[], type: string) => this.Workbooks.refreshExtracts(ids, type),
      (schedule: ISchedule) => this.workbookErrorDetailFactory(schedule),
      allowedMode
    );
  }

  public createExtractsForWorkbooks(workbooks: Set<IWorkbookBase>, context: IActionContext): ng.IPromise<IResult> {
    const scope = <ICreateExtractTasksScope> context.scope.$new();
    let notificationTranslationIds: INotificationTranslationIds = {
      successId: 'extractCreationTasksSuccess',
      errorId: 'extractCreationTasksError'
    };
    return this.Workbooks.createExtracts(workbooks.keys())
      .then(this.ActionHelpers.notifyStandardError(workbooks, notificationTranslationIds,
        (errorItem: IErrorItem<IWorkbookBase>) => this.workbookErrorDetailFactory(null)(errorItem)))
      .finally(() => scope.$destroy());
  }

  public createExtractsForDatasources(datasources: Set<IDataSourceBase>, context: IActionContext): ng.IPromise<IResult> {
    const scope = <ICreateExtractTasksScope> context.scope.$new();
    let notificationTranslationIds: INotificationTranslationIds = {
      successId: 'extractCreationTasksSuccess',
      errorId: 'extractCreationTasksError'
    };
    return this.Datasources.createExtracts(datasources.keys())
      .then(this.ActionHelpers.notifyStandardError(datasources, notificationTranslationIds,
        (errorItem: IErrorItem<IDataSourceBase>) => this.datasourceErrorDetailFactory(null)(errorItem)))
      .finally(() => scope.$destroy());
  }

  private workbookErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IWorkbookBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: this.ContentUrl.forItem(errorItem.item, WorkbookContentType),
        errorMessage: this.workbookErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private workbookErrorMessageFor(errorItem: IErrorItem<IWorkbookBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return this.$translate.instant('extractTasksWorkbookNoExtract');
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return this.$translate.instant('extractTasksWorkbookNoIncrementalExtract');
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return this.$translate.instant('extractTasksRefreshAlreadyExistsForWorkbook', {
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('extractTasksWorkbookErrorInsufficientPermissions');
    }
    return this.createExtractErrorMessageFor(errorItem, schedule);
  }

  // Datasource specific logic

  public forDatasource(datasource: IDataSourceBase, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const datasourceSet = Set.singleton(datasource, (ds: IDataSourceBase) => ds.id);
    return this.forDatasources(datasourceSet, context, allowedMode);
  }

  public forDatasources(datasources: Set<IDataSourceBase>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {

    if (!this.anyRefreshableDatasources(datasources)) {
      return this.showCannotRefreshErrorAlert(datasources);
    }

    const schedulePrompt = (datasources.size() === 1)
      ? this.$translate.instant('schedulesLabelPromptDatasource', { DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name) })
      : this.$translate.instant('schedulesLabelPromptDatasources', { COUNT: datasources.size() }, L10n.Interpolations.Count);

    const refreshNowPrompt =
      this.$translate.instant('extractTasksRefreshNowOnDatasourcesPrompt', {
        DATASOURCE_COUNT: datasources.size(),
        DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name)
      }, L10n.Interpolations.Count);

    return this.execute(
      datasources,
      context,
      schedulePrompt,
      (ids: string[], scheduleId: string, type: string) => this.Datasources.createExtractTask(ids, scheduleId, type),
      refreshNowPrompt,
      (ids: string[], type: string) => this.Datasources.refreshExtracts(ids, type),
      (schedule: ISchedule) => this.datasourceErrorDetailFactory(schedule),
      allowedMode
    );
  }

  private anyRefreshableDatasources(datasources: Set<IDataSourceBase>) {
    return _.any(datasources.toArray(), (ds: IDataSourceBase) => this.isRefreshableDatasource(ds));
  }

  public isRefreshableDatasource(datasource: IDataSourceBase) {
    // To be refreshable, a datasource must be:
    //   1. Not embedded in a workbook
    //   2. Have an extract

    // Embedded datasources are not refreshable
    if (_.isObject(datasource.workbook)) {
      return false;
    }

    // Live datasources are not refreshable
    if (!datasource.hasExtracts) {
      return false;
    }

    return true;
  }

  private showCannotRefreshErrorAlert(datasources: Set<IDataSourceBase>) {

    // Determine which message to display

    const allEmbedded = datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook));
    const anyEmbedded = datasources.any((ds: IDataSourceBase) => _.isObject(ds.workbook));

    let messageId = 'refreshExtracts_cannotRefreshAlertMessage_allLive';
    if (allEmbedded) {
      messageId = 'refreshExtracts_cannotRefreshAlertMessage_allEmbedded';
    }
    else if (anyEmbedded) {
      messageId = 'refreshExtracts_cannotRefreshAlertMessage_mixedEmbeddedAndLive';
    }

    // Show alert

    return this.ModalService.alert({
      titleId: 'refreshExtracts_cannotRefreshAlertTitle',
      messageId: messageId
    }).result;
  }

  private datasourceErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IDataSourceBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: this.ContentUrl.forItem(errorItem.item, DatasourceContentType),
        errorMessage: this.datasourceErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private datasourceErrorMessageFor(errorItem: IErrorItem<IDataSourceBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return this.$translate.instant('extractTasksDatasourceNoExtract');
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return this.$translate.instant('extractTasksDatasourceNoIncrementalExtract');
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return this.$translate.instant('extractTasksRefreshAlreadyExistsForDatasource', {
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('extractTasksDatasourceErrorInsufficientPermissions');
      case UnifiedDatasourceErrorCode.OperationOnlySupportedForPublishedDatasource:
        return this.$translate.instant('errorOperationOnlySupportedForPublishedDatasource');
      case RemoteAgentErrorCode.NoAgentAssignedToDataSource:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNotAssigned');
      case RemoteAgentErrorCode.AgentNotConnected:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNotConnected');
      case RemoteAgentErrorCode.AgentNeedsUpdateToSupportFeature:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNeedsUpdate');
    }
    return this.createExtractErrorMessageFor(errorItem, schedule);
  }

  public forContent(content: Set<IContentInfo>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const workbooksAndDatasources: Set<ExtractType> = new Set(Set.PluckId);
    const ValidTypes = Set.fromKeyArray([WorkbookDefinition.contentType, DatasourceDefinition.contentType]);
    content.each(item => {
      if (ValidTypes.containsKey(item.contentType)) {
        workbooksAndDatasources.add(item as ExtractType);
      }
    });

    const contentCount = workbooksAndDatasources.size();
    const firstItemName = L10n.elideIfLong(workbooksAndDatasources.first().name);
    const schedulePrompt = this.$translate.instant('schedulesLabelPromptContentItems', {
      CONTENT_COUNT: contentCount,
      CONTENT_NAME: firstItemName
    }, L10n.Interpolations.Count);
    const refreshNowPrompt = this.$translate.instant('extractTasksRefreshNowOnContentPrompt', {
      CONTENT_COUNT: contentCount,
      CONTENT_NAME: L10n.elideIfLong(firstItemName)
    }, L10n.Interpolations.Count);

    return this.execute(
      workbooksAndDatasources.clone(),
      context,
      schedulePrompt,
      (ids: string[], scheduleId: string, type: string) => this.createExtractTasksForContent(workbooksAndDatasources, scheduleId, type),
      refreshNowPrompt,
      (ids: string[], type: string) => this.refreshExtractsForContent(workbooksAndDatasources, type),
      (schedule: ISchedule) => this.contentErrorDetailFactory(schedule),
      allowedMode
    );
  }

  private createExtractTasksForContent = (content: Set<ExtractType>, scheduleId: string, type: string): ng.IPromise<IResult> => {
    const workbooksPromise = this.Workbooks.createExtractTask(content.filter(et => WorkbookDefinition.contentType === et.contentType).map(w => w.id), scheduleId, type);
    const datasourcesPromise = this.Datasources.createExtractTask(content.filter(et => DatasourceDefinition.contentType === et.contentType).map(d => d.id), scheduleId, type);

    return this.$q.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  private refreshExtractsForContent = (content: Set<IContentInfo>, type: string): ng.IPromise<IResult> => {
    const workbooksPromise = this.Workbooks.refreshExtracts(content.filter(et => WorkbookDefinition.contentType === et.contentType).map(w => w.id), type);
    const datasourcesPromise = this.Datasources.refreshExtracts(content.filter(et => DatasourceDefinition.contentType === et.contentType).map(d => d.id), type);

    return this.$q.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  private contentErrorDetailFactory = (schedule: ISchedule): (errorItem: IErrorItem<ExtractType>) => IErrorDetail => {
    const workbookErrorDetailFactory = this.workbookErrorDetailFactory(schedule);
    const datasourceErrorDetailFactory = this.datasourceErrorDetailFactory(schedule);

    return (errorItem: IErrorItem<ExtractType>): IErrorDetail => {
      return WorkbookDefinition.contentType === errorItem.item.contentType ?
        workbookErrorDetailFactory(errorItem as IErrorItem<IWorkbookBase>) :
        datasourceErrorDetailFactory(errorItem as IErrorItem<IDataSourceBase>);
    };
  }

}

Module.instance().service('CreateExtractTasksAction', CreateExtractTasksAction);
